import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { toastSuccess } from '../utils/toasts';
import Input from './atoms/Input';
import Switch from './atoms/Switch';
import Button from './atoms/Button';
import Chip from './atoms/Chip';
import TicketDTO, { CreateTicketDTO } from '../models/ticket';
import {
  RootState,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useGetAudienceDiscountsQuery,
  useGetSellerGroupsQuery,
  useGetBusinessEventsQuery,
  useGetScannerTypesQuery,
  useAddTicketToScannerTypeMutation,
  useRemoveTicketFromScannerTypeMutation,
} from '../store';
import Accordion from './atoms/Accordion';
import { formatDateForInput } from '../utils/dates';
import EventDTO from '../models/event';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import CustomModal from './atoms/CustomModal';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import CurrencyInput from './atoms/CurrencyInput';
import Select from './atoms/Select/Select';
import useGetUserRole from '../hooks/useGetUserRole';
import { ScannerTypeTicketDTO } from '../models/scanner-type';
import { scannerTypesApi } from '../store/apis/scanner-types';
import { UserRole } from '../models/user';

interface Props {
  ticket?: TicketDTO;
  onSuccess: () => void;
  onCancel: () => void;
  showtimeId: string | null;
}

interface FormData {
  isEnabled: boolean;
  name: string;
  quantity: number;
  price: number;
  entriesGenerated: number;
  maxPerOrder: number;
  hasOpenPrice: boolean | undefined;
  minOpenPrice: number | null | undefined;
  isExtra: boolean | undefined;
  description: string | undefined;
  isOnlyAdmin: boolean | undefined;
  salesStartsAt: string | null | undefined;
  salesEndsAt: string | null | undefined;
  scanningStartsAt: string | null | undefined;
  scanningEndsAt: string | null | undefined;
  expiredMessage: string | null | undefined;
  isOnlySeller: boolean | undefined;
  hasDiscounts: boolean | undefined;
  isMemberExclusive: boolean | undefined;
  selectedSellerGroups: string[];
  eventIdExclusive: string | null | undefined;
  nextTicketId: string | null | undefined;
}

const getValidationSchema = (event: EventDTO | null) => {
  const validationSchema = yup.object().shape({
    isEnabled: yup.boolean().required(),
    name: yup.string().required('Debes ingresar un nombre'),
    description: yup.string().optional().max(50, 'La descripción no puede tener más de 50 caracteres'),
    quantity: yup
      .number()
      .required('Debes ingresar una cantidad de entradas')
      .typeError('Debes ingresar un número'),
    price: yup
      .number()
      .required('Debes ingresar un precio')
      .typeError('Debes ingresar un número'),
    entriesGenerated: yup
      .number()
      .required('Debes ingresar un número')
      .typeError('Debes ingresar un número'),
    maxPerOrder: yup
      .number()
      .max(10, 'Para evitar fraudes, te recomendamos que no sea mayor a 4.')
      .required('Debes ingresar un número')
      .typeError('Debes ingresar un número'),
    isExtra: yup.boolean().optional(),
    isOnlyAdmin: yup.boolean().optional(),
    isOnlySeller: yup.boolean().optional(),
    salesStartsAt: yup.string().nullable().optional()
      .test(
        'is-before-end-event',
        'La fecha debe ser anterior a la fecha de fin del evento',
        (value) => {
          const end = formatDateForInput(event?.ends_at ?? new Date().toISOString());
          if (!end || !value) {
            return true;
          }
          return new Date(value) <= new Date(end);
        },
      ),
    salesEndsAt: yup.string().nullable().optional()
      .test(
        'is-after-start',
        'La fecha de fin de venta debe ser posterior a la fecha de inicio de venta y anterior a la fecha de fin del evento',
        (value, testContext) => {
          const start = testContext.parent.salesStartsAt;
          const end = formatDateForInput(event?.ends_at ?? null);
          if (!start || !value || !end) {
            return true;
          }
          if (end) {
            return new Date(value) >= new Date(start) && new Date(value) <= new Date(end);
          }
          return new Date(value) >= new Date(start);
        },
      ),
    scanningStartsAt: yup.string().nullable().optional(),
    scanningEndsAt: yup.string().nullable().optional()
      .test(
        'is-after-start-event',
        'La fecha debe ser posterior a la fecha de inicio del evento',
        (value) => {
          const start = formatDateForInput(event?.date ?? new Date().toISOString());
          if (!start || !value) {
            return true;
          }
          return new Date(value) >= new Date(start);
        },
      ),
    hasOpenPrice: yup.boolean().optional(),
    expiredMessage: yup.string().nullable().optional(),
    minOpenPrice: yup
      .number()
      .nullable()
      .optional()
      .when('hasOpenPrice', {
        is: true,
        then: (schema) => schema
          .required('Debes ingresar un precio abierto mínimo')
          .typeError('Debes ingresar un número'),
        otherwise: (schema) => schema.optional().notRequired().nullable(),
      }),
    hasDiscounts: yup.boolean().optional(),
    isMemberExclusive: yup.boolean().optional(),
    selectedSellerGroups: yup.array().of(yup.string().required()).optional().default([]),
    eventIdExclusive: yup.string().nullable().optional(),
    nextTicketId: yup.string().nullable().optional(),
  });
  return validationSchema;
};

const CreateTicket: React.FC<Props> = ({
  ticket, onSuccess, onCancel, showtimeId,
}) => {
  const { event } = useGetSelectedEvent();
  const { business } = useGetSelectedBusiness();
  const {
    register, handleSubmit, formState, setValue, watch, control,
  } = useForm<FormData>({
    defaultValues: {
      isEnabled: ticket ? ticket.active : true,
      name: ticket?.name,
      quantity: ticket?.total_amount,
      price: ticket?.price,
      entriesGenerated: ticket?.items_create || 1,
      maxPerOrder: ticket?.max_per_order,
      hasOpenPrice: ticket?.has_open_price,
      minOpenPrice: ticket?.min_open_price ?? 0,
      isExtra: ticket?.is_extra,
      description: ticket?.description,
      isOnlyAdmin: ticket?.is_only_admin,
      salesStartsAt: formatDateForInput(ticket?.sales_starts_at ?? null),
      salesEndsAt: formatDateForInput(ticket?.sales_ends_at ?? null),
      scanningStartsAt: formatDateForInput(ticket?.scanning_starts_at ?? null),
      scanningEndsAt: formatDateForInput(ticket?.scanning_ends_at ?? null),
      expiredMessage: ticket?.expired_message ?? null,
      isOnlySeller: ticket?.is_seller_ticket ?? true,
      hasDiscounts: ticket?.has_audience_discounts ?? false,
      isMemberExclusive: ticket?.is_member_exclusive ?? false,
      selectedSellerGroups: ticket?.seller_groups?.map((sg) => sg.id) ?? [],
      eventIdExclusive: ticket?.event_id_exclusive || null,
      nextTicketId: ticket?.next_ticket_id || null,
    },
    resolver: yupResolver(getValidationSchema(event)),
  });

  const hasOpenPrice = watch('hasOpenPrice');
  const tickets = useSelector((state: RootState) => state.tickets.tickets);
  const { event_id: eventId, business_id: businessId } = useParams();

  const [createTicket, resCreateTicket] = useCreateTicketMutation();
  const [updateTicket, resUpdateTicket] = useUpdateTicketMutation();
  const [isCombo, setIsCombo] = useState(false);
  const [selectedSellerGroups, setSelectedSellerGroups] = useState<string[]>(
    ticket?.seller_groups?.map((sg) => sg.id) ?? [],
  );

  const { data: sellerGroups } = useGetSellerGroupsQuery({ businessId: event?.business_id || '' }, { skip: !event || !event.seller_sales_enabled });

  const { data: audienceDiscounts } = useGetAudienceDiscountsQuery({ businessId: businessId || '', eventId: eventId || '' }, { skip: !businessId || !eventId });

  const { data: businessEventsData } = useGetBusinessEventsQuery({ businessId: businessId || '' }, { skip: !businessId });
  const businessEvents = businessEventsData?.data || [];

  const userRole = useGetUserRole();
  const isRoot = userRole === UserRole.ROOT;
  const dispatch = useDispatch();
  const { data: scannerTypes } = useGetScannerTypesQuery(
    { businessId: businessId || '', eventId: eventId || '' },
    { skip: !businessId || !eventId || !isRoot },
  );
  const [addTicketToScannerType] = useAddTicketToScannerTypeMutation();
  const [removeTicketFromScannerType] = useRemoveTicketFromScannerTypeMutation();
  const [selectedScannerTypeIds, setSelectedScannerTypeIds] = useState<string[]>([]);
  const [originalScannerTypeAssignments, setOriginalScannerTypeAssignments] = useState<Map<string, ScannerTypeTicketDTO>>(new Map());

  useEffect(() => {
    if (!scannerTypes || !ticket || !isRoot || !businessId || !eventId) {
      return () => {};
    }
    let cancelled = false;

    const fetchAssignments = async () => {
      const assignmentMap = new Map<string, ScannerTypeTicketDTO>();
      const assignedIds: string[] = [];

      for (const st of scannerTypes) {
        // eslint-disable-next-line no-await-in-loop
        const result = await dispatch(
          scannerTypesApi.endpoints.getScannerTypeTickets.initiate({
            businessId,
            eventId,
            scannerTypeId: st.id,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          }) as any,
        );
        if (cancelled) {
          return;
        }
        if (result.data) {
          const match = (result.data as ScannerTypeTicketDTO[]).find(
            (t) => t.ticket_id === ticket.id,
          );
          if (match) {
            assignedIds.push(st.id);
            assignmentMap.set(st.id, match);
          }
        }
      }

      if (!cancelled) {
        setSelectedScannerTypeIds(assignedIds);
        setOriginalScannerTypeAssignments(assignmentMap);
      }
    };

    fetchAssignments();
    return () => { cancelled = true; };
  }, [scannerTypes, ticket, isRoot, businessId, eventId, dispatch]);

  const syncScannerTypeAssignments = async (ticketId: string) => {
    if (!isRoot || !businessId || !eventId) {
      return;
    }

    const toAdd = selectedScannerTypeIds.filter(
      (id) => !originalScannerTypeAssignments.has(id),
    );
    const toRemove = Array.from(originalScannerTypeAssignments.entries()).filter(
      ([stId]) => !selectedScannerTypeIds.includes(stId),
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promises: Promise<unknown>[] = [];
    for (const stId of toAdd) {
      promises.push(
        addTicketToScannerType({
          businessId, eventId, scannerTypeId: stId, ticketId,
        }).unwrap(),
      );
    }
    for (const [, assignment] of toRemove) {
      promises.push(
        removeTicketFromScannerType({
          businessId, eventId, scannerTypeId: assignment.scanner_type_id, scannerTypeTicketId: assignment.id,
        }).unwrap(),
      );
    }

    await Promise.allSettled(promises);
  };

  useEffect(() => {
    if (resCreateTicket.isSuccess) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const createdTicketId = (resCreateTicket.data as any)?.data?.id as string | undefined;
      if (isRoot && createdTicketId && selectedScannerTypeIds.length > 0) {
        syncScannerTypeAssignments(createdTicketId).finally(() => {
          toastSuccess('Entrada creada correctamente');
          onSuccess();
        });
        return () => {};
      }
      const timeoutId = setTimeout(() => {
        toastSuccess('Entrada creada correctamente');
        onSuccess();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    if (resUpdateTicket.isSuccess && ticket) {
      syncScannerTypeAssignments(ticket.id).finally(() => {
        toastSuccess('Entrada actualizada correctamente');
        onSuccess();
      });
      return () => {};
    }
    return () => {};
  }, [resCreateTicket.isSuccess, resUpdateTicket.isSuccess]);

  if (!tickets || !eventId || !businessId) {
    throw new Error('tickets is null or eventID or businessId is null');
  }

  const parseFormToDto = (data: FormData, includeSellerGroups?: boolean, sellerGroupsToSend?: string[]): CreateTicketDTO => {
    const dto: CreateTicketDTO = {
      name: data.name,
      price: data.price,
      total_amount: data.quantity,
      active: data.isEnabled,
      items_create: data.entriesGenerated,
      max_per_order: data.maxPerOrder,
      has_open_price: data.hasOpenPrice ?? false,
      min_open_price: data.hasOpenPrice ? data.minOpenPrice : null,
      is_extra: data.isExtra ?? false,
      description: data.description,
      is_only_admin: data.isOnlyAdmin ?? false,
      sales_starts_at: data.salesStartsAt ? new Date(data.salesStartsAt).toISOString() : null,
      sales_ends_at: data.salesEndsAt ? new Date(data.salesEndsAt).toISOString() : null,
      scanning_starts_at: data.scanningStartsAt ? new Date(data.scanningStartsAt).toISOString() : null,
      scanning_ends_at: data.scanningEndsAt ? new Date(data.scanningEndsAt).toISOString() : null,
      expired_message: data.expiredMessage,
      is_seller_ticket: data.isOnlySeller ?? false,
      has_audience_discounts: data.hasDiscounts ?? false,
      is_member_exclusive: data.isMemberExclusive ?? false,
      showtime_id: showtimeId || null,
      event_id_exclusive: data.eventIdExclusive || null,
      next_ticket_id: data.nextTicketId || null,
    };

    if (includeSellerGroups) {
      dto.seller_groups = sellerGroupsToSend;
    }

    return dto;
  };

  const [isOnBoardingModalOpen, setIsOnBoardingModalOpen] = useState(false);
  const [isPriceWarningModalOpen, setIsPriceWarningModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  const location = useLocation();
  const isOnBoarding = location.pathname.includes('onboarding');
  const navigate = useNavigate();

  const processFormSubmission = async (data: FormData) => {
    let includeSellerGroups = false;
    let sellerGroupsToSend: string[] | undefined;

    if (ticket) {
      // For updates: check if seller groups have changed
      const originalGroups = ticket.seller_groups?.map((sg) => sg.id) || [];
      const currentGroups = selectedSellerGroups || [];

      // Compare arrays to see if they're different
      const hasChanged = originalGroups.length !== currentGroups.length
        || originalGroups.some((id) => !currentGroups.includes(id))
        || currentGroups.some((id) => !originalGroups.includes(id));

      if (hasChanged) {
        includeSellerGroups = true;
        sellerGroupsToSend = currentGroups.length > 0 ? currentGroups : [];
      }
    } else if (selectedSellerGroups && selectedSellerGroups.length > 0) {
      // For new tickets: include seller groups only if there are selected groups
      includeSellerGroups = true;
      sellerGroupsToSend = selectedSellerGroups;
    }

    const parsedTicket = parseFormToDto(data, includeSellerGroups, sellerGroupsToSend);

    if (ticket) {
      updateTicket({
        ticket: { ...parsedTicket, id: ticket.id },
        eventId,
        businessId,
      });
    } else {
      createTicket({
        ticket: parsedTicket,
        eventId,
        businessId,
      });
      if (isOnBoarding) {
        setIsOnBoardingModalOpen(true);
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    if (data.price < 1000) {
      setPendingFormData(data);
      setIsPriceWarningModalOpen(true);
      return;
    }

    await processFormSubmission(data);
  };

  const handleConfirmLowPrice = async () => {
    if (pendingFormData) {
      setIsPriceWarningModalOpen(false);
      await processFormSubmission(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleCancelLowPrice = () => {
    setIsPriceWarningModalOpen(false);
    setPendingFormData(null);
  };

  return (
    <div className={`${isOnBoarding ? '' : 'pt-4'}`}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full overflow-y-auto">
        <div className="mb-2">
          <div className="font-medium text-xl">
            Datos de la entrada
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 my-2">
              <div>En caso de estar activo, las entradas serán visibles para los clientes desde la plataforma.</div>
              <Switch
                label="Visible al público"
                labelClassName="font-medium"
                checked={watch('isEnabled')}
                onChange={(e) => setValue('isEnabled', e.target.checked)}
              />
            </div>
            <Input
              {...register('name', { required: true })}
              id="name"
              label="Nombre de la entrada *"
              placeholder="Máximo 40 caracteres"
              labelAbove={true}
              errMsg={formState.errors.name?.message}
            />
            <Input
              {...register('description', { required: false })}
              id="description"
              label="Descripción"
              placeholder="Máximo 50 caracteres"
              labelAbove={true}
              errMsg={formState.errors.description?.message}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
              <Input
                {...register('quantity', { required: true })}
                id="quantity"
                type="number"
                label="Cantidad *"
                labelAbove={true}
                errMsg={formState.errors.quantity?.message}
              />
              <Input
                {...register('maxPerOrder', { required: true })}
                id="maxPerOrder"
                type="number"
                label="Cantidad máxima por compra *"
                labelAbove={true}
                errMsg={formState.errors.maxPerOrder?.message}
              />
            </div>
              <CurrencyInput
                {...register('price', { required: true })}
                id="price"
                label="Precio *"
                value={watch('price')}
                onChange={(value) => setValue('price', value)}
                labelAbove={true}
                placeholder=" "
                disabled={ticket ? (ticket.amount_taken ?? 0) > 0 : false}
                errMsg={formState.errors.price?.message}
              />
          </div>
        </div>
        <div className="mt-2">
          <Accordion title="Configuraciones avanzadas" noFrame padding="p-0" titleClassName="text-xl p-3 pl-0">
            <div>
              <div className="mb-2">
                <div className="font-medium   text-xl mb-2">
                  Inicio y fin de venta
                </div>
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                    <Input
                      {...register('salesStartsAt')}
                      id="salesStartsAt"
                      type="datetime-local"
                      label="Inicio venta"
                      labelAbove={true}
                      errMsg={formState.errors.salesStartsAt?.message}
                    />
                    <Input
                      {...register('salesEndsAt')}
                      id="salesEndsAt"
                      type="datetime-local"
                      label="Fin venta"
                      labelAbove={true}
                      errMsg={formState.errors.salesEndsAt?.message}
                    />
                  </div>
                </div>
              </div>
              <div className="mb-2">
                <div className="font-medium text-xl mb-2">Límite de ingreso</div>
                <div className="flex flex-col gap-2">
                  <Input
                    {...register('scanningEndsAt')}
                    id="scanningEndsAt"
                    type="datetime-local"
                    label="Horario límite para ingresar"
                    errMsg={formState.errors.scanningEndsAt?.message}
                    labelAbove={true}
                  />
                  <div>
                    <Input
                      {...register('expiredMessage')}
                      id="expiredMessage"
                      type="text"
                      label="Mensaje pasado el horario"
                      labelAbove={true}
                      errMsg={formState.errors.expiredMessage?.message}
                    />
                  </div>
                  <div>
                    <Controller
                      name="nextTicketId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Entrada siguiente"
                          id="nextTicketId"
                          value={field.value || ''}
                          onChange={(e) => setValue('nextTicketId', e.target.value || null)}
                          options={[
                            { label: 'Ninguna', value: '' },
                            ...tickets.filter((t) => t.id !== ticket?.id).map((t) => ({
                              label: t.name,
                              value: t.id,
                            })),
                          ]}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className='text-xl font-medium mb-2'>Ajustes varios</div>
              <div className="mb-4">
                <Switch
                  label="Es extra"
                  checked={watch('isExtra')}
                  onChange={(e) => setValue('isExtra', e.target.checked)}
                  labelClassName="font-medium"
                />
              </div>
              <div className="mb-4">
                <Switch
                  label="Sólo puede ser creada por administradores"
                  checked={watch('isOnlyAdmin')}
                  onChange={(e) => setValue('isOnlyAdmin', e.target.checked)}
                  labelClassName="font-medium"
                />
              </div>
              {event?.seller_sales_enabled && (
                <div className="mb-4">
                  <Switch
                    label="Puede ser vendida por vendedores"
                  checked={watch('isOnlySeller')}
                  onChange={(e) => setValue('isOnlySeller', e.target.checked)}
                  labelClassName="font-medium"
                  />
                </div>
              )}
              {(event?.seller_sales_enabled && watch('isOnlySeller')) && sellerGroups && (
                <div className="mb-5">
                  <Controller
                    name="selectedSellerGroups"
                    control={control}
                    render={() => (
                      <div>
                        <label className="block mb-2">Grupos de vendedores asignados</label>
                        <Select
                          label="Seleccionar grupo de vendedores"
                          id="seller-group-select"
                          value=""
                          onChange={(value) => {
                            const sellerGroupId = value.target.value;
                            if (!selectedSellerGroups.includes(sellerGroupId)) {
                              const newSelectedGroups = [
                                ...selectedSellerGroups,
                                sellerGroupId,
                              ];
                              setSelectedSellerGroups(newSelectedGroups);
                              setValue('selectedSellerGroups', newSelectedGroups);
                            }
                          }}
                          options={
                            sellerGroups
                              .filter(
                                (group) => !selectedSellerGroups.includes(group.id),
                              )
                              .map((group) => ({
                                label: group.name,
                                value: group.id,
                              }))
                          }
                        />
                        <div className="flex flex-wrap gap-2 mt-2">
                          {sellerGroups
                            .filter((group) => selectedSellerGroups.includes(group.id))
                            .map((group) => (
                              <Chip
                                key={group.id}
                                label={group.name}
                                onDelete={() => {
                                  const newSelectedGroups = selectedSellerGroups.filter(
                                    (id) => id !== group.id,
                                  );
                                  setSelectedSellerGroups(newSelectedGroups);
                                  setValue('selectedSellerGroups', newSelectedGroups);
                                }}
                              />
                            ))}
                        </div>
                        {selectedSellerGroups.length === 0 && (
                          <p className="text-sm text-text-tertiary mt-2">
                            No hay grupos seleccionados. La entrada estará disponible para todos los vendedores.
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}
              <div className="mb-4">
                <Switch
                  label="Aplica promoción por cantidad"
                  checked={isCombo}
                  onChange={(e) => setIsCombo(e.target.checked)}
                  labelClassName="font-medium"
                />
              </div>
              {isCombo && (
                <Input
                  {...register('entriesGenerated', { required: true })}
                  id="entriesGenerated"
                  type="number"
                  label="Cantidad de venta en combo"
                  labelAbove={true}
                  errMsg={formState.errors.entriesGenerated?.message}
                />
              )}
              {event?.has_audience_discounts && <div>
                <div className="mt-4">
                  <Switch
                    label="Aplica descuentos por audiencia"
                    checked={watch('hasDiscounts')}
                    onChange={(e) => setValue('hasDiscounts', e.target.checked)}
                    labelClassName="font-medium"
                  />
                </div>
                {watch('hasDiscounts')
                  && <p className='text-sm text-grey-500 mt-2'>
                    *Para esta entrada se aplicará el descuento:{' '}
                    {audienceDiscounts?.data?.filter((discount) => discount.active).map((discount) => discount.name).join(', ')}
                  </p>
                }
              </div>
              }

              {business?.has_members && (
                <div className='mt-4'>
                  <Switch
                    label="Solo puede ser adquirida por socios"
                    checked={watch('isMemberExclusive')}
                    onChange={(e) => setValue('isMemberExclusive', e.target.checked)}
                    labelClassName="font-medium"
                  />
                </div>
              )}

              {isRoot && scannerTypes && scannerTypes.length > 0 && (
                <div className="mb-5 mt-4">
                  <div className="font-medium text-xl mb-2">Tipos de escáner</div>
                  <Select
                    label="Asignar tipo de escáner"
                    id="scanner-type-select"
                    value=""
                    onChange={(value) => {
                      const scannerTypeId = value.target.value;
                      if (scannerTypeId && !selectedScannerTypeIds.includes(scannerTypeId)) {
                        setSelectedScannerTypeIds([...selectedScannerTypeIds, scannerTypeId]);
                      }
                    }}
                    options={
                      scannerTypes
                        .filter((st) => st.is_active && !selectedScannerTypeIds.includes(st.id))
                        .map((st) => ({
                          label: st.name,
                          value: st.id,
                        }))
                    }
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {scannerTypes
                      .filter((st) => selectedScannerTypeIds.includes(st.id))
                      .map((st) => (
                        <Chip
                          key={st.id}
                          label={st.name}
                          onDelete={() => {
                            setSelectedScannerTypeIds(
                              selectedScannerTypeIds.filter((id) => id !== st.id),
                            );
                          }}
                        />
                      ))}
                  </div>
                  {selectedScannerTypeIds.length === 0 && (
                    <p className="text-sm text-text-tertiary mt-2">
                      No hay tipos de escáner asignados. Esta entrada podrá ser escaneada por todos los escáneres.
                    </p>
                  )}
                </div>
              )}

              <div className='mt-4'>
                <Controller
                  name="eventIdExclusive"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Exclusiva para evento específico"
                      id="event-exclusive-select"
                      value={field.value || ''}
                      onChange={(e) => setValue('eventIdExclusive', e.target.value || null)}
                      options={[
                        { label: 'Ninguno (disponible para todos los eventos)', value: '' },
                        ...businessEvents
                          .filter((businessEvent) => businessEvent.id !== eventId) // Exclude current event
                          .map((businessEvent) => ({
                            label: businessEvent.name,
                            value: businessEvent.id,
                          })),
                      ]}
                    />
                  )}
                />
                {watch('eventIdExclusive') && (
                  <p className='text-sm text-text-tertiary mt-2'>
                    Esta entrada solo podrá comprarse si el cliente compró en el evento seleccionado.
                  </p>
                )}
              </div>
              {event?.has_open_price && (
                <div className="my-4">
                  <Switch
                    label="Tiene precio abierto"
                    checked={watch('hasOpenPrice')}
                    onChange={(e) => setValue('hasOpenPrice', e.target.checked)}
                    labelClassName="font-medium"
                  />
                </div>
              )}
              {hasOpenPrice && (
                <div className="flex flex-col gap-4 mb-4 w-full">
                  <CurrencyInput
                    {...register('minOpenPrice', { required: true })}
                    id="minOpenPrice"
                    label="Precio abierto mínimo"
                    labelAbove={true}
                    value={watch('minOpenPrice') ?? 0}
                    onChange={(value) => setValue('minOpenPrice', value)}
                    errMsg={formState.errors.minOpenPrice?.message}
                  />
                </div>
              )}
            </div>
          </Accordion>

          <div className="flex flex-col justify-between mb-2 mt-5">
            {!isOnBoarding && <div className='flex flex-row gap-2'>
              <Button
                variant='secondary'
                onClick={onCancel}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={resCreateTicket?.isLoading || resUpdateTicket.isLoading}
              >
                Guardar
              </Button>
            </div>}
            {isOnBoarding && <div className='flex flex-row gap-2'>
              <Button
                variant='secondary'
                onClick={() => navigate(`/admin/${businessId}/events/${eventId}/`)}
              >
                Omitir
              </Button>
              <Button
                variant='primary'
                type="submit"
              >
                Crear entrada
              </Button>
            </div>}
          </div>
        </div>
      </form >
      <CustomModal isOpen={isOnBoardingModalOpen} onRequestClose={() => {}} hideCancelButton>
        <div className='flex flex-col gap-2 max-w-[358px]'>
          <h1>¡Felicitaciones!</h1>
          <h1>¡Tu proceseo de alta fue un éxito!</h1>
          <p>Lograste completar todos los pasos del onboarding de avantti. Serás redirigido a la vista del evento donde podrás conocer las diferentes secciones del mismo.</p>
          <p>Ahora solo queda esperar que tu evento sea aprobado para comenzar a vender. No te preocupes, no demorará más de unas horas.</p>
        </div>
        <div className="flex flex-row gap-2 justify-between mt-4">
          <Button
            variant="primary"
            onClick={() => navigate(`/admin/${businessId}/events/${eventId}/`)}
          >
            Aceptar
          </Button>
        </div>
      </CustomModal>
      <CustomModal isOpen={isPriceWarningModalOpen} onRequestClose={handleCancelLowPrice} hideCancelButton>
        <div className='flex flex-col gap-3 w-[55vw] max-w-2xl'>
          <h1 className="text-xl font-medium">Precio bajo detectado</h1>
          <p className="text-text-secondary">
            El precio ingresado es menor a $1.000. ¿Estás seguro de que deseas continuar con este precio?
          </p>
          <div className="flex flex-row gap-2">
            <Button
              variant="secondary"
              onClick={handleCancelLowPrice}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmLowPrice}
              loading={resCreateTicket?.isLoading || resUpdateTicket.isLoading}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </CustomModal>
    </div >
  );
};

export default CreateTicket;
